import { doc, getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase/config';
import * as Icons from 'lucide-react';

export interface ContentType {
  value: string;
  label: string;
  icon: any;
  color: string;
}

export interface Platform {
  value: string;
  label: string;
  icon: any;
  color: string;
}

// Default content types (fallback if none configured)
const DEFAULT_CONTENT_TYPES: ContentType[] = [
  { value: 'image', label: 'Image', icon: Icons.Image, color: 'bg-blue-500' },
  { value: 'video', label: 'Video', icon: Icons.Video, color: 'bg-red-500' },
  { value: 'blog', label: 'Blog Post', icon: Icons.FileText, color: 'bg-purple-500' },
  { value: 'infographic', label: 'Infographic', icon: Icons.Layers, color: 'bg-green-500' },
  { value: 'carousel', label: 'Carousel', icon: Icons.Grid, color: 'bg-orange-500' },
];

// Default platforms (fallback if none configured)
const DEFAULT_PLATFORMS: Platform[] = [
  { value: 'instagram', label: 'Instagram', icon: Icons.Camera, color: 'bg-gradient-to-r from-purple-500 to-pink-500' },
  { value: 'facebook', label: 'Facebook', icon: Icons.Users, color: 'bg-blue-600' },
  { value: 'twitter', label: 'Twitter', icon: Icons.MessageSquare, color: 'bg-black' },
  { value: 'linkedin', label: 'LinkedIn', icon: Icons.Briefcase, color: 'bg-blue-700' },
  { value: 'tiktok', label: 'TikTok', icon: Icons.Video, color: 'bg-black' },
  { value: 'youtube', label: 'YouTube', icon: Icons.Play, color: 'bg-red-600' },
];

class ContentConfigService {
  private static instance: ContentConfigService;
  private contentTypesCache: ContentType[] | null = null;
  private platformsCache: Platform[] | null = null;
  private lastFetch: number = 0;
  private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  public static getInstance(): ContentConfigService {
    if (!ContentConfigService.instance) {
      ContentConfigService.instance = new ContentConfigService();
    }
    return ContentConfigService.instance;
  }

  private shouldRefreshCache(): boolean {
    return Date.now() - this.lastFetch > this.CACHE_DURATION;
  }

  private deserializeIcon(iconData: any): any {
    if (typeof iconData === 'string') {
      // If it's a string, try to get the icon from Icons
      return (Icons as any)[iconData] || Icons.FileText;
    }
    if (iconData && iconData.name) {
      // If it has a name property, use that
      return (Icons as any)[iconData.name] || Icons.FileText;
    }
    // Fallback to default icon
    return Icons.FileText;
  }

  public async getContentTypes(): Promise<ContentType[]> {
    if (this.contentTypesCache && !this.shouldRefreshCache()) {
      return this.contentTypesCache;
    }

    try {
      const contentTypesDoc = await getDoc(doc(db, 'settings', 'content_types'));
      if (contentTypesDoc.exists()) {
        const data = contentTypesDoc.data();
        if (data.types && Array.isArray(data.types)) {
          this.contentTypesCache = data.types.map((type: any) => ({
            ...type,
            icon: this.deserializeIcon(type.icon)
          }));
          this.lastFetch = Date.now();
          return this.contentTypesCache;
        }
      }
    } catch (error) {
      console.error('Error fetching content types:', error);
    }

    // Return defaults if no custom types found
    this.contentTypesCache = DEFAULT_CONTENT_TYPES;
    return this.contentTypesCache;
  }

  public async getPlatforms(): Promise<Platform[]> {
    if (this.platformsCache && !this.shouldRefreshCache()) {
      return this.platformsCache;
    }

    try {
      const platformsDoc = await getDoc(doc(db, 'settings', 'platforms'));
      if (platformsDoc.exists()) {
        const data = platformsDoc.data();
        if (data.platforms && Array.isArray(data.platforms)) {
          this.platformsCache = data.platforms.map((platform: any) => ({
            ...platform,
            icon: this.deserializeIcon(platform.icon)
          }));
          this.lastFetch = Date.now();
          return this.platformsCache;
        }
      }
    } catch (error) {
      console.error('Error fetching platforms:', error);
    }

    // Return defaults if no custom platforms found
    this.platformsCache = DEFAULT_PLATFORMS;
    return this.platformsCache;
  }

  public clearCache(): void {
    this.contentTypesCache = null;
    this.platformsCache = null;
    this.lastFetch = 0;
  }

  public invalidateContentTypesCache(): void {
    this.contentTypesCache = null;
  }

  public invalidatePlatformsCache(): void {
    this.platformsCache = null;
  }

  public async getContentTypeByValue(value: string): Promise<ContentType | undefined> {
    const contentTypes = await this.getContentTypes();
    return contentTypes.find(type => type.value === value);
  }

  public async getPlatformByValue(value: string): Promise<Platform | undefined> {
    const platforms = await this.getPlatforms();
    return platforms.find(platform => platform.value === value);
  }
}

export const contentConfigService = ContentConfigService.getInstance();
